import { Box, Button, Modal, Typography } from "@material-ui/core";
import { Check, X } from "phosphor-react";
import { useEffect, useState } from "react";
import { fget, fpost } from "../../API/callsAPI";
import Loader from "../Utilities/Loader/Loader";
import usePickPlanModalStyles from "./PickPlanModalStyles";
import ManageDowngradeModal from "./ManageDowngradeModal";

// Tier ranking for comparison
const TIER_RANK = {
  free: 1,
  standard: 2,
  professional: 3,
};

const PickPlanModal = ({
  handleClose,
  currentTierDetails,
  teamId,
  handleOpenEnterpriseChooseModal,
  setSnackValue,
  teamDetails,
}) => {
  const classes = usePickPlanModalStyles();

  const [plans, setPlans] = useState([]);
  const [isPlansLoading, setIsPlansLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isManageDowngradeModalOpen, setIsManageDowngradeModalOpen] =
    useState(false);

  // Fetch plans
  const handleGetPlans = async () => {
    try {
      setIsPlansLoading(true);
      const res = await fget({ url: "project-space/plans/" });
      setPlans(res?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsPlansLoading(false);
    }
  };

  useEffect(() => {
    handleGetPlans();
  }, []);

  const BYTES_IN_GB = 1024 * 1024 * 1024;

  const checkDowngradeConstraints = ({ currentTierDetails, selectedPlan }) => {
    const issues = [];

    // Users check
    if (currentTierDetails?.total_users > Number(selectedPlan?.seats)) {
      issues.push("users");
    }

    // Storage check (convert GB → bytes)
    const allowedStorageBytes = Number(selectedPlan?.storage) * BYTES_IN_GB;

    if (currentTierDetails?.total_storage > allowedStorageBytes) {
      issues.push("storage");
    }

    return {
      hasIssues: issues.length > 0,
      issues,
    };
  };

  // Handle choose plan click
  const handleChoosePlan = async (plan) => {
    if (!plan?.tier || !currentTierDetails?.tier) return;

    const currentRank = TIER_RANK[currentTierDetails.tier];
    const selectedRank = TIER_RANK[plan.tier];

    if (currentRank === selectedRank) return;

    const isUpgrade = selectedRank > currentRank;

    if (isUpgrade) {
      // 🔼 Upgrade (unchanged)
      try {
        setIsActionLoading(true);
        const res = await fpost({
          url: `project-space/${teamId}/upgrade-tier/`,
          data: { tier: plan.tier },
        });

        setSnackValue({
          isOpen: true,
          message: "Invoice Generated, Please check your email",
          isError: false,
        });

        if (res?.data?.type === "checkout_session_created") {
          window.open(res.data.url);
        }

        handleClose?.();
      } catch (error) {
        console.error(error);
      } finally {
        setIsActionLoading(false);
      }
    } else {
      // 🔽 Downgrade logic
      const { hasIssues, issues } = checkDowngradeConstraints({
        currentTierDetails,
        selectedPlan: plan,
      });

      setSelectedPlan(plan);

      if (hasIssues) {
        // ❌ Usage exceeds selected plan → show modal
        setIsManageDowngradeModalOpen(true);
      } else {
        // ✅ Safe to downgrade → API call
        try {
          setIsActionLoading(true);

          await fpost({
            url: `project-space/${teamId}/downgrade-tier/`,
            data: { tier: plan.tier },
          });

          setSnackValue({
            isOpen: true,
            message: "Plan downgraded successfully",
            isError: false,
          });
          window.location.reload();
          handleClose?.();
        } catch (error) {
          console.error(error);
        } finally {
          setIsActionLoading(false);
        }
      }
    }
  };

  return (
    <Box className={classes.modalWrapper}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h6">Pick your plan</Typography>
        <Button onClick={handleClose}>
          <X size={22} />
        </Button>
      </Box>

      {/* Body */}
      {!isPlansLoading ? (
        <Box className={classes.plansWrapper}>
          {plans.map((plan) => {
            const isCurrentPlan = currentTierDetails?.tier === plan?.tier;

            const isUpgrade =
              TIER_RANK[plan.tier] > TIER_RANK[currentTierDetails?.tier];

            return (
              <Box
                key={plan.tier}
                className={classes.planCard}
                style={{
                  border: isCurrentPlan ? "1px solid #6C65D9" : "transparent",
                }}
              >
                <Box className={classes.planContent}>
                  <Typography className={classes.subtitle}>
                    Recommended for{" "}
                    {plan.label === "Free"
                      ? "students"
                      : plan.label === "Standard"
                        ? "small teams"
                        : "studios"}
                  </Typography>

                  <Box className={classes.titleWrapper}>
                    <Typography className={classes.planTitle}>
                      {plan.label}
                    </Typography>
                    <Typography className={classes.price}>
                      {plan.amount > 0 && (
                        <span
                          style={{
                            color: "#6C65D9",
                            textDecoration: "line-through",
                            marginRight: "8px",
                            opacity: 0.8,
                          }}
                        >
                          {plan.currency === "inr" ? "₹" : "$"}
                          {Number(plan.amount) + Number(plan.amount) * 0.2}
                        </span>
                      )}
                      <span
                        style={{
                          color: plan.amount > 0 ? "white" : "",
                          fontWeight: 600,
                        }}
                      >
                        {plan.currency === "inr" ? "₹" : "$"} {plan.amount}
                      </span>{" "}
                      /mo
                    </Typography>
                  </Box>

                  <Box className={classes.featureList}>
                    {[
                      `${plan.seats} Team Seat`,
                      `${plan.seats} Team Project`,
                      `${plan.storage} GB Cloud Storage`,
                    ].map((feature) => (
                      <Box key={feature} className={classes.featureItem}>
                        <Box className={classes.checkIcon}>
                          <Check size={16} color="#6C65D9" weight="bold" />
                        </Box>
                        <Typography className={classes.featureText}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Button
                  disabled={isCurrentPlan || isActionLoading}
                  onClick={() => handleChoosePlan(plan)}
                  className={`${classes.chooseButton} ${
                    isCurrentPlan ? classes.currentPlanButton : ""
                  }`}
                >
                  {isCurrentPlan
                    ? "Current Plan"
                    : isUpgrade
                      ? "Choose Plan"
                      : "Choose Plan"}
                </Button>
              </Box>
            );
          })}
          <Box
            className={classes.planCard}
            style={{
              border:
                currentTierDetails?.tier === "enterprise"
                  ? "1px solid #6C65D9"
                  : "transparent",
            }}
          >
            <Box className={classes.planContent}>
              <Typography className={classes.subtitle}>
                Recommended for large studios & institutions
              </Typography>

              <Box className={classes.titleWrapper}>
                <Typography className={classes.planTitle}>
                  Enterprise
                </Typography>
                <Typography
                  className={classes.price}
                  style={{
                    color: "white",
                  }}
                >
                  Variable
                </Typography>
              </Box>

              <Box className={classes.featureList}>
                {[
                  `Multiple Team Seats`,
                  `Multiple Projects`,
                  `Flexible Cloud Storage with Pay-as-You-Go Pricing`,
                  `Adaptable Credit Options with Pay-as-You-Go Rates`,
                ].map((feature) => (
                  <Box key={feature} className={classes.featureItem}>
                    <Box className={classes.checkIcon}>
                      <Check size={16} color="#6C65D9" weight="bold" />
                    </Box>
                    <Typography className={classes.featureText}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Button
              // disabled={isCurrentPlan || isActionLoading}
              onClick={handleOpenEnterpriseChooseModal}
              className={`${classes.chooseButton} ${
                currentTierDetails?.tier === "enterprise"
                  ? classes.currentPlanButton
                  : ""
              }`}
            >
              {currentTierDetails?.tier === "enterprise"
                ? "Current Plan"
                : "Choose Plan"}
            </Button>
          </Box>
        </Box>
      ) : (
        <Loader />
      )}
      <Modal
        open={isManageDowngradeModalOpen}
        onClose={() => setIsManageDowngradeModalOpen(false)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ManageDowngradeModal
          handleClose={() => setIsManageDowngradeModalOpen(false)}
          setSnackValue={setSnackValue}
          teamDetails={teamDetails}
          selectedPlan={selectedPlan}
        />
      </Modal>
    </Box>
  );
};

export default PickPlanModal;
